import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import {
  AttributeBase,
  FieldsToBeEdited,
  Leg,
  LegPrice,
  LegSyncingFields,
  RfqLegSettings,
  TreasuryDepositRateCurve
} from '@approot/models';
import { SelectorComponent } from '@approot/selector/selector.component';
import {
  AlertsService,
  AttributeControlService,
  LegService,
  LegSyncService,
  ProductsConfigService,
  RfqService,
  RfqTabService,
  StrategyDetailService,
  TradesCaptureService
} from '@approot/services';
import { ModalComponent } from '@approot/ui-kit';
import { clone, Utilities } from '@approot/utilities';
import { Throttle } from 'lodash-decorators';
import { EMPTY, of, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { DynamicLegAttributeComponent } from '../dynamic-leg-attribute/dynamic-leg-attribute.component';
import { PortalService } from '../modals/quote-schedule/quote-schedule-portal.service';
import { QuoteScheduleComponent } from '../modals/quote-schedule/quote-schedule.component';
import { LegBarrierFormListenersService } from './services/leg-barrier-form-listeners.service';
import { LegCrossFormListenersService } from './services/leg-cross-form-listeners.service';
import { LegCrossService } from './services/leg-cross.service';
import { LegCurrencyListenersService } from './services/leg-currency-form-listeners.service';
import { LegCurrencyService } from './services/leg-currency.service';
import { LegDateAttributeService } from './services/leg-date-attribute.service';
import { LegDateListenersService } from './services/leg-date-form-listeners.service';
import { LegDateService } from './services/leg-date.service';
import { LegElemService } from './services/leg-elem.service';
import { LegFwdVolService } from './services/leg-fwd-vol.service';
import { LegHelperService } from './services/leg-helper.service';
import { LegLoadingService } from './services/leg-loading.service';
import { LegMiscellaneousService } from './services/leg-miscellaneous.service';
import { LegNotionalListenersService } from './services/leg-notional-form-listeners.service';
import { LegNotionalService } from './services/leg-notional.service';
import { LegOtherFormListenersService } from './services/leg-other-form-listeners.service';
import { LegPricingService } from './services/leg-pricing.service';
import { LegScheduleFormListenersService } from './services/leg-schedule-form-listeners.service';
import { LegScheduleService } from './services/leg-schedule.service';
import { LegSpotStrikeService } from './services/leg-spot-strike.service';
import { LegStrikeListenersService } from './services/leg-strike-form-listeners.service';
import { LegTargetProfitFormListenersService } from './services/leg-target-profit-form-listeners.service';
import { LegTradeFormListenersService } from './services/leg-trade-form-listeners.service';
import { LegTradingModeService } from './services/leg-trading-mode.service';
import { dcdFormValidator } from './utilities/dcd-utilities';
import { getAttributeControlFromKey } from './utilities/leg-attribute-utilities';
import { setupLegs } from './utilities/leg-loading-utilities';
import {
  makeTextCustomColor,
  onScheduleFieldChange,
  shouldLegUseWebSocket,
  wipeAttributeInfo
} from './utilities/leg.utilities';

// in local dev need to see small changes, so increase the resolution
export const DEFAULT_DECIMAL_PLACES = 4;

@Component({
  selector: 'app-leg',
  templateUrl: './leg.component.html',
  styleUrls: ['./leg.component.scss'],
  providers: [
    LegHelperService,
    LegFwdVolService,
    LegSpotStrikeService,
    LegScheduleService,
    LegDateService,
    LegPricingService,
    LegElemService,
    LegNotionalService,
    LegDateAttributeService,
    LegCrossService,
    LegTradingModeService,
    LegLoadingService,
    LegCurrencyService,
    LegMiscellaneousService,
    LegBarrierFormListenersService,
    LegDateListenersService,
    LegStrikeListenersService,
    LegNotionalListenersService,
    LegCurrencyListenersService,
    LegCrossFormListenersService,
    LegScheduleFormListenersService,
    LegTradeFormListenersService,
    LegOtherFormListenersService,
    LegTargetProfitFormListenersService
  ]
})
export class LegComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges, DoCheck
{
  @Input() firstLegTradeId: number;
  @Input() tradingMode = false;
  @Input() public legIndex: number;
  @Input({ required: true }) public leg: Leg;
  @Input() public legSyncingInfo: LegSyncingFields;
  @Input() public fieldsToBeEdited: FieldsToBeEdited = {
    notional: 'counterNotional',
    notional1: 'counterNotional',
    notional2: 'counterNotional'
  };

  @Input() public rfqExecutionSettings: RfqLegSettings = {
    static: false,
    rfqLeg: false,
    directRfq: false,
    editableSettings: { editableFields: [], lockWhileActive: [] }
  };
  @Input() public enableSpreadAttributeValue = false;
  @Input() public firstCheckedLegIndex: number;
  @Input() public lastCheckedLegIndex: number;
  @Input() public formInert = false;
  @Input() parentTabIndex: number;
  public initialProductType: string;
  @Output() legPresetLoaded = new EventEmitter<boolean>();
  @Output() changeInLeg = new EventEmitter<void>();
  @Output() fieldBlurred = new EventEmitter<string>();
  @Output() changeInLegPrice = new EventEmitter<boolean>();
  @Output() changeInCounterParty = new EventEmitter<void>();
  @Output() dropdownChange = new EventEmitter<void>();
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChildren(DynamicLegAttributeComponent)
  legComponents!: QueryList<DynamicLegAttributeComponent>;
  keyboard: KeyboardEvent;
  form: FormGroup;
  solverOpen = false;
  copyLegOpen = false;
  select: SelectorComponent;
  saveTradeOpen = false;
  hasSpotAlreadyLoaded = false;
  selectedTreasuryCurves: TreasuryDepositRateCurve[];
  selectedTreasuryCurve: TreasuryDepositRateCurve;

  warningScreenData = {
    warningScreenOpen: false,
    messageToDisplay: '',
    headerMessage: '',
    warningScreenType: ''
  };
  public allAttributesBase: AttributeBase<any>[];
  private premiumValueSub: Subscription;
  private premiumSettlementDateSub: Subscription;
  private strategyDetailService = inject(StrategyDetailService);
  private originalLeg: Leg;
  private deleteLeg = false;
  private activeAttributes = this.legService.activeAttributes();
  private lastScheduleOpened = null;
  private lastEmittedRequestId = undefined;

  constructor(
    public tradesCaptureService: TradesCaptureService,
    public changeRef: ChangeDetectorRef,
    public legHelperService: LegHelperService,
    public legFwdVolService: LegFwdVolService,
    public legPricingService: LegPricingService,
    public legSpotStrikeService: LegSpotStrikeService,
    public legElemService: LegElemService,
    public legService: LegService,
    public rfqService: RfqService,
    private attributeControlService: AttributeControlService,
    private webSocketService: GlobalWebsocketService,
    private legSyncService: LegSyncService,
    private productsConfigService: ProductsConfigService,
    private alertsService: AlertsService,
    private legDateService: LegDateService,
    private legTradingModeService: LegTradingModeService,
    private legLoadingService: LegLoadingService,
    private legMiscellaneousService: LegMiscellaneousService,
    private legBarrierFormListenersService: LegBarrierFormListenersService,
    private legDateListenersService: LegDateListenersService,
    private legStrikeListenersService: LegStrikeListenersService,
    private legNotionalListenersService: LegNotionalListenersService,
    private legCurrencyListenersService: LegCurrencyListenersService,
    private legCrossFormListenersService: LegCrossFormListenersService,
    private legScheduleFormListenersService: LegScheduleFormListenersService,
    private legOtherFormListenersService: LegOtherFormListenersService,
    private legTargetProfitFormListenersService: LegTargetProfitFormListenersService,
    private portalService: PortalService,
    private rfqTabService: RfqTabService
  ) {
    this.legHelperService.onChangeInLegFn = this.onChangeInLeg.bind(this);
    this.legHelperService.onChangeInLegPriceFn =
      this.onChangeInLegPrice.bind(this);
    this.legHelperService.onFieldBlurredFn = this.onFieldBlur.bind(this);
    this.legHelperService.onLegPresetLoadedFn =
      this.onLegPresetLoaded.bind(this);
    this.legHelperService.onChangeInCounterParty =
      this.onChangeInCounterParty.bind(this);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.legHelperService.fieldsToBeEdited = this.fieldsToBeEdited;
      this.legHelperService.firstLegTradeId = this.firstLegTradeId;
      this.legHelperService.leg = this.leg;
      this.legHelperService.legIndex = this.legIndex;
      this.legHelperService.legSyncingInfo = this.legSyncingInfo;
      this.legHelperService.rfqExecutionSettings = this.rfqExecutionSettings;
      this.legHelperService.tradingMode = this.tradingMode;
      if (changes.tradingMode && this.form) {
        this.legTradingModeService.changeInTradeView(
          this.tradingMode,
          this.legIndex,
          this.firstLegTradeId,
          this.leg,
          this.allAttributesBase
        );
      }
    }
  }

  public ngDoCheck(): void {
    this.prepareToEmitComponent();
  }

  spreadAttributeValue(key: string, textColor: string) {
    this.legService.spreadAttributeValue(
      key,
      this.form.controls[key].value,
      this.legIndex,
      textColor
    );
  }

  onButtonClick() {
    const executionTypes = this.rfqService.executionContext.executionTypes;
    if (
      this.leg &&
      this.form &&
      this.rfqExecutionSettings.rfqLeg &&
      executionTypes.length &&
      executionTypes[0].shortName === 'genericMultiHedge'
    ) {
      // this.portalService.emitProjectedData(
      //   this.leg,
      //   this.form,
      //   this.rfqExecutionSettings,
      //   this.rfqTabService?.rfqDetailsDTO?.id
      // );
      console.log('createEntry 1');

      console.log(this.parentTabIndex, this.legIndex);

      this.portalService.createEntry({
        leg: this.leg,
        form: this.form,
        rfqExecutionSettings: this.rfqExecutionSettings,
        requestId: this.rfqTabService?.rfqDetailsDTO?.id,
        parentTabIndex: this.parentTabIndex,
        parentLegIndex: this.legIndex
      });
    }
  }

  private prepareToEmitComponent() {
    // if (this.lastScheduleOpened?.trim() !== this.leg.scheduleOpened?.trim()) {
    //   this.lastScheduleOpened = this.leg.scheduleOpened;
    //   const executionTypes = this.rfqService.executionContext.executionTypes;
    //   if (
    //     this.leg &&
    //     this.form &&
    //     this.rfqExecutionSettings.rfqLeg &&
    //     executionTypes.length &&
    //     executionTypes[0].shortName === 'genericMultiHedge'
    //   ) {
    //     // this.portalService.emitProjectedData(
    //     //   this.leg,
    //     //   this.form,
    //     //   this.rfqExecutionSettings,
    //     //   this.rfqTabService?.rfqDetailsDTO?.id
    //     // );
    //     console.log('createEntry 1');

    //     this.portalService.createEntry({
    //       leg: this.leg,
    //       form: this.form,
    //       rfqExecutionSettings: this.rfqExecutionSettings,
    //       requestId: this.rfqTabService?.rfqDetailsDTO?.id
    //     });
    //   }
    // }

    if (this.lastEmittedRequestId !== this.rfqTabService?.rfqDetailsDTO?.id) {
      this.lastEmittedRequestId = this.rfqTabService?.rfqDetailsDTO?.id;
      if (
        this.leg &&
        this.form &&
        this.rfqExecutionSettings.rfqLeg &&
        this.lastEmittedRequestId
      ) {
        // this.portalService.emitProjectedData(
        //   this.leg,
        //   this.form,
        //   this.rfqExecutionSettings,
        //   this.rfqTabService?.rfqDetailsDTO?.id
        // );
        console.log(
          'createEntry 2',
          this.parentTabIndex,
          this.rfqTabService?.rfqDetailsDTO?.id
        );
        this.portalService.updateEntryRequestIdByParentTabIndex(
          this.parentTabIndex,
          this.lastEmittedRequestId
        );
      }
    }
  }

  public ngOnDestroy(): void {
    this.legHelperService.legAlive = false;
    this.onChangeInLeg();
    if (this.deleteLeg) {
      this.onChangeInLegPrice(true);
    }
    this.legHelperService.subSink.unsubscribe();
  }

  public ngOnInit(): void {
    this.originalLeg = clone(this.leg);
    this.leg.disablePricingButton = false;
    this.leg.loadingPrices = false;
    if (this.leg.attributesAsFormControls) {
      this.form = this.attributeControlService.toFormGroup(
        this.leg.attributesAsFormControls
      );
      this.allAttributesBase = this.productsConfigService.getAllAttributes(
        this.leg.attributesAsFormControls
      ) as AttributeBase<any>[];
    } else {
      this.form = new FormGroup({});
    }

    if (this.rfqExecutionSettings.rfqLeg) {
      ['strike', 'strike1', 'strike2'].forEach(attr => {
        try {
          this.legSpotStrikeService.reformatStrikeforRFQ(attr, this.leg);
        } catch (err) {
          console.error(`Error in RFQ leg strike formatting ${attr}`, err);
        }
      });
    }

    this.legHelperService.form = this.form;
    if (this.leg.productType === 'newdcd') {
      this.handleDcdValidation();
    }
    this.customFormListeners();
    this.handleTradeAttributes();
    this.webSocketListener(); // listen to websocket

    if (!this.leg.fromTab) {
      this.onChangeInLegPrice();
    }
  }

  public ngAfterViewInit(): void {
    this.legElemService.initListeners(this.legComponents);
    this.legLoadingService.preLoadingSetup(this.allAttributesBase);

    // should just be  *if (this.leg.preSetPosition)* - for backwards compatibility
    setTimeout(() => {
      if (this.leg.preSetPosition) {
        // without the timeout, loading the leg will trigger a FURTHER modification of the data before Angular
        // could display it on the screen, so its not clear what values should be shown in the leg,
        // so we defer the code inside ngAfterViewInit to another Javascript turn with setTimeout
        this.legLoadingService.loadLegPresets(this.legIndex);
        // currency needs to be reset here, as its options only get set once the
        // currencyPair has been set
        this.legLoadingService.postLoadingPresets(this.originalLeg);
        this.legFwdVolService.syncForwardPoints();
        this.syncPremiumDates();
        this.syncPremiumValues();
        if (
          !this.legSyncingInfo?.forwardPoints &&
          !this.leg.legAttributeDataFromTab
          // !this.leg.isLegCopied
        ) {
          this.legFwdVolService.initForwardPointVolCalls();
        }
      } else {
        this.legHelperService.presetsLoaded = true;
        this.legHelperService.onLegPresetLoadedFn();
      }
      if (!this.leg.isLegCopied) {
        this.legLoadingService.refreshAttributesOnLoad();
      }
      this.legTradingModeService.updateTradeAttributeElements(
        this.allAttributesBase
      );
      this.legLoadingService.restoreAttributesData();
      this.leg.isLegCopied = false;
    });
  }

  public descriptionClick(): void {
    this.modal?.open();
  }

  public onScheduleFieldChange(scheduleResponseData: {
    changedAttributes: string[];
    scheduleName: string;
  }): void {
    onScheduleFieldChange(
      this.form,
      this.leg,
      scheduleResponseData.scheduleName,
      scheduleResponseData.changedAttributes
    );
    this.legMiscellaneousService.legDetailsChanged(); // legs data has changed wipe data regarding the previous status
  }

  public onApplySolver(solverAction: { type: string; value: number }): void {
    if (this.form.get(solverAction.type)) {
      this.leg.shortcutValueUsed[solverAction.type] = null;
      this.leg.usingLatestValues[solverAction.type] = false;
      wipeAttributeInfo(solverAction.type, this.leg);
      this.form.get(solverAction.type).setValue(solverAction.value);
      if (solverAction.type === 'spot') {
        this.leg.usingLatestValues.spot = false;
        makeTextCustomColor(
          solverAction.type,
          Utilities.orangeTextColor,
          this.leg
        );
      } else if (solverAction.type === 'secondSpot') {
        this.leg.usingLatestValues.secondSpot = false;
        makeTextCustomColor(
          solverAction.type,
          Utilities.orangeTextColor,
          this.leg
        );
      }
    }
  }

  public onDuplicateClick(): void {
    this.onCopyLegOpenChanged(true);
  }

  public onDCDVanillaEmitted(): void {
    //Should be called once DCD product has been priced
    let childData = this.leg.pricedLeg.childVanillaOption; // JSON object for vanilla option
    this.legService.addLeg({
      productName: 'vanillaoption',
      newProduct: true,
      preSetPosition: childData,
      tradeViewMode: this.tradingMode
    });
  }

  public onCopyLegEmitted(productType: string): void {
    this.legMiscellaneousService.onCopyLegEmitted(
      productType,
      this.tradingMode
    );
  }

  public onCopyLegOpenChanged(event): void {
    this.copyLegOpen = event;
  }

  // open as modal
  public onSolverClick(): void {
    this.onSolverOpenChanged(true);
  }

  public onSolverOpenChanged(event): void {
    this.solverOpen = event;
  }

  public setBasketModalState(modalState: boolean): void {
    this.legElemService.setBasketModalState(modalState);
  }

  public setOptionsPortfolioModalState(modalState): void {
    this.legElemService.setOptionsPortfolioModalState(!!modalState);
  }

  public setOptionsPortfolioSpotsModalState(modalState): void {
    this.legElemService.setOptionsPortfolioSpotsModalState(!!modalState);
  }

  public optionsPortfolioRequestSpot(event: {
    control: FormControl;
    cross: string;
  }): void {
    // get the lastest spot for the form control
    this.legSpotStrikeService.spotCaller(event.cross, event.control, true);
  }

  public onSaveTradeClick() {
    if (this.tradingMode && this.form.valid) {
      this.tradesCaptureService
        .tradeIdExistChecker(this.form.get('tradeId').value)
        .subscribe(r => {
          const exists = r.tradeIdExists;
          if (exists) {
            this.warningScreenData.warningScreenType = 'save';
            this.warningScreenData.warningScreenOpen = true;
            this.warningScreenData.headerMessage = 'TradeId already exists';
            this.warningScreenData.messageToDisplay =
              'tradeID: ' +
              this.form.get('tradeId').value +
              ' already exists. Do you want to overwrite it?';
          } else {
            this.legTradingModeService.tradeCaptureSave();
          }
        });
    } else if (!this.tradingMode) {
      this.onSaveTradeOpenChanged(true);
    }
  }

  public warningScreenResponse(val: boolean): void {
    this.warningScreenData.warningScreenOpen = false;
    switch (this.warningScreenData.warningScreenType) {
      case 'save':
        if (val) {
          this.legTradingModeService.tradeCaptureSave();
        }
        break;
      case 'delete':
        if (val) {
          this.tradeCaptureRemove();
        } else {
          this.removeLeg();
        }
        break;
    }
  }

  public warningOpenChanged(val: boolean): void {
    this.warningScreenData.warningScreenOpen = val;
  }

  public onSaveTradeOpenChanged(event): void {
    this.saveTradeOpen = event;
  }

  public onRemoveLeg(): void {
    const tradeId = this.form.get('tradeId')?.value;
    if (this.tradingMode && tradeId) {
      this.tradesCaptureService.tradeIdExistChecker(tradeId).subscribe(r => {
        const exists = r.tradeIdExists;
        if (exists) {
          this.warningScreenData.warningScreenType = 'delete';
          this.warningScreenData.headerMessage = 'Please Confirm';
          this.warningScreenData.messageToDisplay =
            'Delete tradeID ' + tradeId + '?';
          this.warningScreenData.warningScreenOpen = true;
        } else {
          this.removeLeg();
        }
      });
    } else {
      this.removeLeg();
    }
  }

  public checkboxClicked(checked: boolean): void {
    // when the leg checkbox is clicked
    this.leg.checked = checked;
    this.onChangeInLeg();
    this.legHelperService.onChangeInLegPriceFn();
    this.onChangeInCounterParty();
  }

  public onShowMidToggleEmit(event: boolean): void {
    this.leg.isMidToggled = event;
    if (this.leg.checked) {
      this.legHelperService.onChangeInLegPriceFn();
    }
  }

  public onPricingSubmit(): void {
    this.legPricingService.onPricingSubmit(this.legComponents);
  }

  // the split bid/offer items have complex validity criteria so need
  // to interrogate directly.
  public allLegsValid(): boolean {
    return this.legHelperService.allLegsValid(this.legComponents);
  }

  @Throttle(500)
  public get isLegDisabledTmpl(): boolean {
    return this.isLegDisabled;
  }

  public get isLegDisabled(): boolean {
    return (
      !this.form?.valid || this.leg.disablePricingButton || !this.allLegsValid()
    );
  }

  public showResult(
    result:
      | 'dcdSpecifications'
      | 'markUpSpecifications'
      | 'marketDataSpecifications'
      | 'resultSpecifications'
  ): number {
    return this.productsConfigService.pricingResultsInfo[result]?.length;
  }

  public onDropdownChange(): void {
    this.dropdownChange.emit();
  }

  //**************** PRIVATE ************************/

  private webSocketListener(): void {
    if (shouldLegUseWebSocket(this.leg)) {
      this.legHelperService.subSink.sink = this.webSocketService
        .getListener<LegPrice>({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.legHelperService.streamingWebsocketUUID,
          take: Infinity
        })
        .pipe(
          catchError(_ => {
            this.legPricingService.pricingFailed();
            return EMPTY;
          }),
          switchMap(r => {
            if (!r || r.error) {
              this.legPricingService.pricingFailed();
              return EMPTY;
            }
            return of(r);
          })
        )
        .subscribe({
          next: r => {
            if (r.payload.statusCode === 400) {
              this.alertsService.addAlert({ message: r.payload.body });
              this.legPricingService.pricingFailed();
            } else {
              this.legPricingService.handlePricingResults(r.payload);
            }
          },
          complete: async () => {
            this.webSocketListener();
          }
        });
    }
  }

  private removeLeg(): void {
    this.deleteLeg = true;
    this.legService.legs.splice(this.legIndex, 1);
    this.legService.updateActiveAttributes('remove', {}, this.tradingMode);
  }

  private customFormListeners(): void {
    this.legStrikeListenersService.strikeListeners();
    this.legBarrierFormListenersService.barrierListeners();
    this.legDateListenersService.dateListeners();
    this.legNotionalListenersService.notionalListeners();
    this.legCurrencyListenersService.currencyListeners();
    this.legCrossFormListenersService.crossListeners();
    this.legScheduleFormListenersService.scheduleListeners(); // these are listeners that are related to the schedule.
    //NOTE the date schedule listeners are in the date listener function
    // these are listeners that only have one version of its type.
    this.legTargetProfitFormListenersService.targetProfitListeners();
    setupLegs(this.form, this.leg);
    this.legOtherFormListenersService.otherListeners();
    this.legMiscellaneousService.depositTakerSetup();
  }

  private tradeCaptureRemove(): void {
    const tradeId = this.form.get('tradeId').value.toString();
    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'deleteTrade',
        tradeId
      })
      .subscribe(_ => {
        this.tradesCaptureService.addRemoveLocalTrade('deleteTrade', tradeId);
        this.removeLeg();
      });
  }

  private syncPremiumValues(): void {
    if (this.legSyncingInfo?.premiumValueType) {
      const premiumValue = this.form.get('premiumValueType');
      const attribute = getAttributeControlFromKey(
        'premiumSettlementDate',
        this.leg.attributesAsFormControls
      );
      const premiumValueTypeCtrl = this.form.get('premiumValueType');
      this.legHelperService.subSink.sink =
        this.legSyncService.premiumValueTypes$.subscribe(x => {
          this.premiumValueSub?.unsubscribe();
          this.premiumValueSub = x[this.legIndex].subscribe(val => {
            if (premiumValueTypeCtrl.value !== val) {
              premiumValueTypeCtrl.setValue(val, { emitEvent: false });
              if (premiumValue.value === 'Spot') {
                attribute.readOnly = true;
              } else {
                this.legDateService.copySettlementDateToPremiumDate();
              }
            }
          });
          this.legHelperService.subSink.sink = this.premiumValueSub;
        });
    }
  }

  private syncPremiumDates(): void {
    if (this.legSyncingInfo?.premiumSettlementDate) {
      const premiumSettlementDateCtrl = this.form.get('premiumSettlementDate');
      this.legHelperService.subSink.sink =
        this.legSyncService.premiumSettlementDates$.subscribe(
          settlementDatesObs => {
            this.premiumSettlementDateSub?.unsubscribe();
            this.premiumSettlementDateSub = settlementDatesObs[
              this.legIndex
            ].subscribe(date => {
              if (premiumSettlementDateCtrl.value !== date) {
                premiumSettlementDateCtrl.setValue(date, { emitEvent: false });
              }
            });
            this.legHelperService.subSink.sink = this.premiumSettlementDateSub;
          }
        );
    }
  }

  private handleDcdValidation(): void {
    this.form.setValidators((ctrl: AbstractControl) => {
      return dcdFormValidator(this.leg, ctrl, this.tradingMode);
    });
  }

  private handleTradeAttributes(): void {
    this.legTradingModeService.handleTradeAttributes(
      this.tradingMode,
      this.firstLegTradeId,
      this.allAttributesBase
    );
  }

  private onChangeInLeg(): void {
    this.changeInLeg.emit(); // need allow for leg changes to be detected outside of leg
  }

  private onChangeInLegPrice(legRemoved = false): void {
    if (!this.legHelperService.presetsLoaded && this.leg.fromTab) {
      return;
    }
    this.changeInLegPrice.emit(legRemoved);
  }

  private onFieldBlur(fieldName: string): void {
    this.fieldBlurred.emit(fieldName);
  }

  private onLegPresetLoaded(): void {
    this.legPresetLoaded.emit(this.legHelperService.presetsLoaded);
  }

  private onChangeInCounterParty(): void {
    this.changeInCounterParty.emit();
  }
}
